/**
 * Google Analytics 4 (GA4) + Google Tag Manager (GTM) Analytics Module
 *
 * This module provides typed functions for tracking:
 * - Ecommerce events (credit pack purchase funnel)
 * - Virtual currency spend events (board/theme purchases)
 * - Page views for SPA navigation
 * - Client ID capture for backend Measurement Protocol attribution
 */

// Type definitions for dataLayer events
declare global {
  interface Window {
    dataLayer: DataLayerEvent[];
    gtag?: (...args: unknown[]) => void;
  }
}

// GA4 Ecommerce Item
export interface GA4EcommerceItem {
  item_id: string;
  item_name: string;
  item_category?: string;
  item_category2?: string;
  price: number;
  quantity: number;
  coupon?: string;
}

// GA4 Ecommerce Event Base
interface GA4EcommerceBase {
  currency: string;
  value: number;
  items: GA4EcommerceItem[];
}

// DataLayer event types
type DataLayerEvent =
  | { event: 'page_view'; page_path: string; page_title: string }
  | { event: 'view_item_list'; ecommerce: GA4EcommerceBase & { item_list_id?: string; item_list_name?: string } }
  | { event: 'view_item'; ecommerce: GA4EcommerceBase }
  | { event: 'select_item'; ecommerce: GA4EcommerceBase & { item_list_id?: string; item_list_name?: string } }
  | { event: 'add_to_cart'; ecommerce: GA4EcommerceBase }
  | { event: 'begin_checkout'; ecommerce: GA4EcommerceBase }
  | { event: 'add_payment_info'; ecommerce: GA4EcommerceBase & { payment_type?: string } }
  | { event: 'purchase'; ecommerce: GA4EcommerceBase & { transaction_id: string; coupon?: string } }
  | { event: 'spend_credits'; spend_id: string; currency: 'CREDITS'; value: number; item_type: string; item_id: string; item_name: string; transaction_context: string }
  | { ecommerce: null } // For clearing ecommerce object before new events
  | { event: string; [key: string]: unknown };

// Credit Pack interface matching our product types
export interface CreditPack {
  id: string;
  lookupKey: string;
  name: string;
  description?: string;
  credits: number;
  price: number; // in cents
  currency?: string;
}

// Board/Theme interface for spend tracking
export interface SpendableItem {
  id: string;
  name: string;
  type: 'board' | 'theme';
  creditsCost: number;
  boardId?: string; // For themes applied to boards
}

// Get the app environment for event tagging
function getAppEnv(): string {
  return import.meta.env.VITE_APP_ENV || import.meta.env.MODE || 'development';
}

// Get GA4 Measurement ID from environment
function getMeasurementId(): string {
  return import.meta.env.VITE_GA4_MEASUREMENT_ID || '';
}

// Initialize dataLayer if not already present
function ensureDataLayer(): void {
  if (typeof window !== 'undefined') {
    window.dataLayer = window.dataLayer || [];
  }
}

// Push event to dataLayer
function pushToDataLayer(event: DataLayerEvent): void {
  ensureDataLayer();
  if (typeof window !== 'undefined') {
    // Clear ecommerce object before pushing new ecommerce event
    if ('ecommerce' in event) {
      window.dataLayer.push({ ecommerce: null });
    }
    window.dataLayer.push(event);

    if (import.meta.env.DEV) {
      console.log('[Analytics] dataLayer push:', event);
    }
  }
}

// Send event via gtag with debug logging
function sendGtagEvent(eventName: string, eventData: Record<string, unknown>): void {
  if (typeof window !== 'undefined') {
    if (window.gtag) {
      window.gtag('event', eventName, eventData);
      if (import.meta.env.DEV) {
        console.log('[Analytics] gtag event:', eventName, eventData);
      }
    } else if (import.meta.env.DEV) {
      console.warn('[Analytics] gtag not available - is GA4 script loaded?');
    }
  }
}

/**
 * Convert credit pack to GA4 ecommerce item
 */
function creditPackToItem(pack: CreditPack, coupon?: string): GA4EcommerceItem {
  return {
    item_id: `credits_pack_${pack.id}`,
    item_name: pack.name,
    item_category: 'Credits',
    price: pack.price / 100, // Convert cents to dollars
    quantity: 1,
    ...(coupon && { coupon }),
  };
}

// ============================================================================
// Page View Tracking
// ============================================================================

/**
 * Track a page view event for SPA navigation
 * Call this on route changes in React Router
 */
export function trackPageView(path: string, title?: string): void {
  const pageTitle = title || document.title;

  // Send to GA4 via gtag (direct tracking)
  sendGtagEvent('page_view', {
    page_path: path,
    page_title: pageTitle,
  });

  // Also push to dataLayer for GTM triggers
  pushToDataLayer({
    event: 'page_view',
    page_path: path,
    page_title: pageTitle,
  });
}

// ============================================================================
// Credit Pack Purchase Funnel Events (Ecommerce)
// ============================================================================

/**
 * Track when credit pack list is viewed
 * Trigger: When user opens the credits/pricing page
 */
export function trackViewItemList(packs: CreditPack[]): void {
  const items = packs.map((pack) => creditPackToItem(pack));
  const totalValue = items.reduce((sum, item) => sum + item.price, 0);

  const ecommerceData = {
    item_list_id: 'credit_packs',
    item_list_name: 'Credit Packs',
    currency: 'USD',
    value: totalValue,
    items,
  };

  // Send to GA4 via gtag (direct tracking)
  sendGtagEvent('view_item_list', ecommerceData);

  // Also push to dataLayer for GTM triggers
  pushToDataLayer({
    event: 'view_item_list',
    ecommerce: ecommerceData,
  });
}

/**
 * Track when a specific credit pack is viewed in detail
 * Trigger: When user clicks on a credit pack to see details
 */
export function trackViewItem(pack: CreditPack): void {
  const item = creditPackToItem(pack);

  const ecommerceData = {
    currency: 'USD',
    value: item.price,
    items: [item],
  };

  // Send to GA4 via gtag (direct tracking)
  sendGtagEvent('view_item', ecommerceData);

  // Also push to dataLayer for GTM triggers
  pushToDataLayer({
    event: 'view_item',
    ecommerce: ecommerceData,
  });
}

/**
 * Track when user selects a credit pack
 * Trigger: When user clicks "Select" or similar action on a pack
 */
export function trackSelectItem(pack: CreditPack): void {
  const item = creditPackToItem(pack);

  const ecommerceData = {
    item_list_id: 'credit_packs',
    item_list_name: 'Credit Packs',
    currency: 'USD',
    value: item.price,
    items: [item],
  };

  // Send to GA4 via gtag (direct tracking)
  sendGtagEvent('select_item', ecommerceData);

  // Also push to dataLayer for GTM triggers
  pushToDataLayer({
    event: 'select_item',
    ecommerce: ecommerceData,
  });
}

/**
 * Track when credit pack is added to cart
 * Trigger: When user confirms pack selection before checkout
 */
export function trackAddToCart(pack: CreditPack): void {
  const item = creditPackToItem(pack);

  const ecommerceData = {
    currency: 'USD',
    value: item.price,
    items: [item],
  };

  // Send to GA4 via gtag (direct tracking)
  sendGtagEvent('add_to_cart', ecommerceData);

  // Also push to dataLayer for GTM triggers
  pushToDataLayer({
    event: 'add_to_cart',
    ecommerce: ecommerceData,
  });
}

/**
 * Track when checkout is initiated
 * Trigger: When checkout modal opens or checkout page loads
 */
export function trackBeginCheckout(pack: CreditPack, coupon?: string): void {
  const item = creditPackToItem(pack, coupon);

  const ecommerceData = {
    currency: 'USD',
    value: item.price,
    items: [item],
  };

  // Send to GA4 via gtag (direct tracking)
  sendGtagEvent('begin_checkout', ecommerceData);

  // Also push to dataLayer for GTM triggers
  pushToDataLayer({
    event: 'begin_checkout',
    ecommerce: ecommerceData,
  });
}

/**
 * Track when payment information step is shown
 * Trigger: When Stripe payment form is displayed
 */
export function trackAddPaymentInfo(pack: CreditPack, coupon?: string): void {
  const item = creditPackToItem(pack, coupon);

  const ecommerceData = {
    currency: 'USD',
    value: item.price,
    payment_type: 'card', // Stripe uses card payments
    items: [item],
  };

  // Send to GA4 via gtag (direct tracking)
  sendGtagEvent('add_payment_info', ecommerceData);

  // Also push to dataLayer for GTM triggers
  pushToDataLayer({
    event: 'add_payment_info',
    ecommerce: ecommerceData,
  });
}

// Note: The 'purchase' event is sent from the BACKEND via Measurement Protocol
// to ensure it only fires on confirmed payments (webhook-verified)
// Do NOT send purchase events from frontend to avoid duplicates

// ============================================================================
// Theme Store Events (Virtual Currency Purchases)
// ============================================================================

// Theme interface for analytics
export interface ThemeItem {
  id: string;
  name: string;
  category?: string;
  creditPrice: number;
}

/**
 * Convert theme to GA4 ecommerce item (for virtual currency tracking)
 */
function themeToItem(theme: ThemeItem): GA4EcommerceItem {
  return {
    item_id: `theme_${theme.id}`,
    item_name: theme.name,
    item_category: 'Themes',
    item_category2: theme.category,
    price: theme.creditPrice, // Price in credits (virtual currency)
    quantity: 1,
  };
}

/**
 * Track when theme list is viewed
 * Trigger: When user opens the theme store
 */
export function trackThemeViewItemList(themes: ThemeItem[]): void {
  const items = themes.map((theme) => themeToItem(theme));
  const totalValue = items.reduce((sum, item) => sum + item.price, 0);

  const ecommerceData = {
    item_list_id: 'theme_store',
    item_list_name: 'Theme Store',
    currency: 'CREDITS', // Virtual currency
    value: totalValue,
    items,
  };

  sendGtagEvent('view_item_list', ecommerceData);
  pushToDataLayer({
    event: 'view_item_list',
    ecommerce: ecommerceData,
  });
}

/**
 * Track when a specific theme is viewed in detail
 * Trigger: When user clicks on a theme to see details / purchase dialog
 */
export function trackThemeViewItem(theme: ThemeItem): void {
  const item = themeToItem(theme);

  const ecommerceData = {
    currency: 'CREDITS',
    value: item.price,
    items: [item],
  };

  sendGtagEvent('view_item', ecommerceData);
  pushToDataLayer({
    event: 'view_item',
    ecommerce: ecommerceData,
  });
}

/**
 * Track when theme is added to cart (user clicks purchase button)
 * Trigger: When user clicks "N Credits" button to buy theme
 */
export function trackThemeAddToCart(theme: ThemeItem): void {
  const item = themeToItem(theme);

  const ecommerceData = {
    currency: 'CREDITS',
    value: item.price,
    items: [item],
  };

  sendGtagEvent('add_to_cart', ecommerceData);
  pushToDataLayer({
    event: 'add_to_cart',
    ecommerce: ecommerceData,
  });
}

/**
 * Track when theme checkout/purchase dialog is opened
 * Trigger: When purchase confirmation dialog opens
 */
export function trackThemeBeginCheckout(theme: ThemeItem): void {
  const item = themeToItem(theme);

  const ecommerceData = {
    currency: 'CREDITS',
    value: item.price,
    items: [item],
  };

  sendGtagEvent('begin_checkout', ecommerceData);
  pushToDataLayer({
    event: 'begin_checkout',
    ecommerce: ecommerceData,
  });
}

/**
 * Track when theme purchase is confirmed (credits spent)
 * Trigger: After successful theme purchase
 */
export function trackThemePurchase(theme: ThemeItem, transactionId: string): void {
  const item = themeToItem(theme);

  const ecommerceData = {
    transaction_id: transactionId,
    currency: 'CREDITS',
    value: item.price,
    items: [item],
  };

  sendGtagEvent('purchase', ecommerceData);
  pushToDataLayer({
    event: 'purchase',
    ecommerce: ecommerceData,
  });
}

// ============================================================================
// Credit Spend Events (Virtual Currency - NOT revenue)
// ============================================================================

/**
 * Track when user starts the credit spend flow for boards/themes
 * This is an optional frontend event for funnel analysis
 */
export function trackSpendBeginCheckout(item: SpendableItem, context: string): void {
  pushToDataLayer({
    event: 'spend_credits',
    spend_id: '', // Will be set by backend
    currency: 'CREDITS',
    value: item.creditsCost,
    item_type: item.type,
    item_id: `${item.type}_${item.id}`,
    item_name: item.name,
    transaction_context: context,
  });
}

// Note: The authoritative 'spend_credits' event is sent from the BACKEND
// via Measurement Protocol after credits are actually deducted

// ============================================================================
// GA4 Client ID Capture
// ============================================================================

/**
 * Get GA4 client_id for backend attribution
 * The client_id links frontend browsing sessions to backend purchase events
 *
 * Priority:
 * 1. gtag API (most reliable when available)
 * 2. _ga cookie parsing (fallback)
 *
 * @returns Promise<string | null> - The client_id or null if unavailable
 */
export async function getGA4ClientId(): Promise<string | null> {
  // Method 1: Try gtag API first
  const measurementId = getMeasurementId();
  if (measurementId && typeof window !== 'undefined' && window.gtag) {
    try {
      const clientId = await new Promise<string | null>((resolve) => {
        const timeout = setTimeout(() => resolve(null), 2000); // 2 second timeout

        window.gtag!('get', measurementId, 'client_id', (cid: string) => {
          clearTimeout(timeout);
          resolve(cid || null);
        });
      });

      if (clientId) {
        return clientId;
      }
    } catch {
      // Fall through to cookie method
    }
  }

  // Method 2: Parse _ga cookie
  if (typeof document !== 'undefined') {
    const gaCookie = document.cookie
      .split('; ')
      .find((row) => row.startsWith('_ga='));

    if (gaCookie) {
      // _ga cookie format: GA1.1.XXXXXXXXXX.XXXXXXXXXX
      // client_id is the last two number segments joined by '.'
      const parts = gaCookie.split('=')[1]?.split('.');
      if (parts && parts.length >= 4) {
        return `${parts[2]}.${parts[3]}`;
      }
    }
  }

  return null;
}

/**
 * Get client_id synchronously from cookie (less reliable but faster)
 * Use getGA4ClientId() for best results
 */
export function getGA4ClientIdSync(): string | null {
  if (typeof document === 'undefined') return null;

  const gaCookie = document.cookie
    .split('; ')
    .find((row) => row.startsWith('_ga='));

  if (gaCookie) {
    const parts = gaCookie.split('=')[1]?.split('.');
    if (parts && parts.length >= 4) {
      return `${parts[2]}.${parts[3]}`;
    }
  }

  return null;
}

// ============================================================================
// Utility: Environment Info for Backend Events
// ============================================================================

/**
 * Get analytics context to pass to backend
 * Include this in checkout and spend API requests
 */
export async function getAnalyticsContext(): Promise<{
  gaClientId: string | null;
  appEnv: string;
}> {
  const gaClientId = await getGA4ClientId();
  return {
    gaClientId,
    appEnv: getAppEnv(),
  };
}

/**
 * Sync version of getAnalyticsContext for immediate use
 */
export function getAnalyticsContextSync(): {
  gaClientId: string | null;
  appEnv: string;
} {
  return {
    gaClientId: getGA4ClientIdSync(),
    appEnv: getAppEnv(),
  };
}

// ============================================================================
// Homepage Button Click Tracking
// ============================================================================

/**
 * Track homepage button clicks for conversion funnel analysis
 * @param buttonName - Descriptive name of the button (e.g., 'hero_get_started', 'buy_credits')
 * @param section - Section of homepage where button appears (e.g., 'hero', 'pricing', 'cta')
 * @param destination - Where the button navigates to (e.g., '/register', '/credits')
 */
export function trackHomepageClick(buttonName: string, section: string, destination: string): void {
  const eventData = {
    event_category: 'homepage',
    button_name: buttonName,
    section: section,
    destination: destination,
  };

  sendGtagEvent('homepage_click', eventData);
  pushToDataLayer({
    event: 'homepage_click',
    ...eventData,
  });
}

// ============================================================================
// Debug Mode
// ============================================================================

/**
 * Enable debug logging for analytics events
 * Only active in development mode
 */
export function enableDebugMode(): void {
  if (import.meta.env.DEV) {
    const originalPush = window.dataLayer.push.bind(window.dataLayer);
    window.dataLayer.push = function (...args: DataLayerEvent[]) {
      console.log('[Analytics]', ...args);
      return originalPush(...args);
    };
  }
}

// Auto-enable debug mode in development
if (import.meta.env.DEV && typeof window !== 'undefined') {
  ensureDataLayer();
}
